import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Calendar, FileText, Mail, Users, Send, AlertCircle, Server } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import LoadingSpinner from '../../common/LoadingSpinner';
import { getResendFiles, initiateResend } from '../../../services/resendService';
import { getReportById } from '../../../services/reportServiceSwitch';
import './ReportManagement.css';

/**
 * ResendReportModal Component
 *
 * Multi-step modal for resending a previously processed report:
 *  Step 1: Select date
 *  Step 2: Select file from processed/error list
 *  Step 3: Select recipients (all, specific emails, groups)
 *  Step 4: Confirm and submit
 */
export default function ResendReportModal({ isOpen, onClose, report, onSuccess }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState('');
  const [files, setFiles] = useState({ processed_files: [], error_files: [] });
  const [selectedFile, setSelectedFile] = useState(null);
  const [recipientType, setRecipientType] = useState('all');
  const [selectedEmailIds, setSelectedEmailIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [fullReport, setFullReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen && report) {
      setStep(1);
      setDate('');
      setFiles({ processed_files: [], error_files: [] });
      setSelectedFile(null);
      setRecipientType('all');
      setSelectedEmailIds([]);
      setSelectedGroupIds([]);
      setError(null);
      loadFullReport();
    }
  }, [isOpen, report]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFullReport = async () => {
    try {
      const data = await getReportById(report.id);
      setFullReport(data);
    } catch {
      setFullReport(null);
    }
  };

  const fetchFiles = useCallback(async () => {
    if (!date || !report) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getResendFiles(report.id, date);
      setFiles(result);
      setSelectedFile(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch files');
      setFiles({ processed_files: [], error_files: [] });
    } finally {
      setLoading(false);
    }
  }, [date, report]);

  const handleDateSubmit = () => {
    if (!date) return;
    fetchFiles();
    setStep(2);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    // SFTP reports don't need recipient selection — skip to confirm
    const isSftp = fullReport?.deliveryMethod === 'SFTP' || report?.deliveryMethod === 'SFTP';
    setStep(isSftp ? 4 : 3);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    try {
      setSubmitting(true);
      setError(null);
      const payload = {
        source_s3_key: selectedFile.s3Key,
        recipient_type: recipientType
      };
      if (recipientType === 'selected') {
        payload.email_ids = selectedEmailIds;
        payload.group_ids = selectedGroupIds;
      }
      await initiateResend(report.id, payload);
      onSuccess?.('Resend initiated successfully. The report will be re-delivered shortly.');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to initiate resend');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEmailId = (id) => {
    setSelectedEmailIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleGroupId = (id) => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString();
  };

  const allFiles = [...files.processed_files, ...files.error_files];
  const hasSelectedRecipients = recipientType === 'all' ||
    (selectedEmailIds.length > 0 || selectedGroupIds.length > 0);

  if (!report) return null;

  const renderStep1 = () => (
    <div className="resend-step">
      <p className="resend-step-desc">Select the date the report was originally sent.</p>
      <div className="resend-date-picker">
        <label htmlFor="resend-date">
          <Calendar size={16} /> Date
        </label>
        <input
          id="resend-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="resend-date-input"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="resend-step">
      <p className="resend-step-desc">Select a file to resend.</p>
      {loading ? (
        <LoadingSpinner />
      ) : allFiles.length === 0 ? (
        <div className="resend-empty">
          <AlertCircle size={20} />
          <span>No files found for this report on {date}</span>
        </div>
      ) : (
        <div className="resend-file-list">
          {allFiles.map((file) => (
            <button
              key={file.s3Key}
              className={`resend-file-item ${selectedFile?.s3Key === file.s3Key ? 'selected' : ''} ${file.source === 'error' ? 'error-file' : ''}`}
              onClick={() => handleFileSelect(file)}
            >
              <div className="resend-file-info">
                <FileText size={16} />
                <div>
                  <div className="resend-file-name">{file.originalFilename}</div>
                  <div className="resend-file-meta">
                    {formatTimestamp(file.lastModified)} · {formatBytes(file.sizeBytes)}
                    {file.source === 'error' && <span className="resend-error-badge">Error</span>}
                  </div>
                  {file.errorLog && (
                    <div className="resend-error-log">{file.errorLog}</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="resend-step">
      <p className="resend-step-desc">Choose who should receive this report.</p>

      <div className="resend-recipient-options">
        <label className="resend-radio-option">
          <input
            type="radio"
            name="recipientType"
            value="all"
            checked={recipientType === 'all'}
            onChange={() => setRecipientType('all')}
          />
          <Send size={16} />
          <span>All original recipients</span>
        </label>

        <label className="resend-radio-option">
          <input
            type="radio"
            name="recipientType"
            value="selected"
            checked={recipientType === 'selected'}
            onChange={() => setRecipientType('selected')}
          />
          <Users size={16} />
          <span>Selected recipients only</span>
        </label>
      </div>

      {recipientType === 'selected' && fullReport && (
        <div className="resend-recipient-lists">
          {fullReport.emails?.length > 0 && (
            <div className="resend-recipient-section">
              <h4><Mail size={14} /> Emails</h4>
              {fullReport.emails.map(email => (
                <label key={email.id} className="resend-checkbox-option">
                  <input
                    type="checkbox"
                    checked={selectedEmailIds.includes(email.id)}
                    onChange={() => toggleEmailId(email.id)}
                  />
                  <span>{email.emailAddress || email.name}</span>
                </label>
              ))}
            </div>
          )}

          {fullReport.groups?.length > 0 && (
            <div className="resend-recipient-section">
              <h4><Users size={14} /> Groups</h4>
              {fullReport.groups.map(group => (
                <label key={group.id} className="resend-checkbox-option">
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(group.id)}
                    onChange={() => toggleGroupId(group.id)}
                  />
                  <span>{group.name}</span>
                </label>
              ))}
            </div>
          )}

          {(!fullReport.emails?.length && !fullReport.groups?.length) && (
            <p className="resend-empty-recipients">No recipients configured for this report.</p>
          )}
        </div>
      )}
    </div>
  );

  const isSftp = fullReport?.deliveryMethod === 'SFTP' || report?.deliveryMethod === 'SFTP';

  const renderStep4 = () => (
    <div className="resend-step">
      <p className="resend-step-desc">Review and confirm the resend.</p>
      <div className="resend-summary">
        <div className="resend-summary-row">
          <span className="resend-summary-label">Report</span>
          <span>{report.name}</span>
        </div>
        <div className="resend-summary-row">
          <span className="resend-summary-label">File</span>
          <span>{selectedFile?.originalFilename}</span>
        </div>
        <div className="resend-summary-row">
          <span className="resend-summary-label">Date</span>
          <span>{date}</span>
        </div>
        <div className="resend-summary-row">
          <span className="resend-summary-label">Delivery</span>
          <span>
            {isSftp ? (
              <>
                <Server size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                SFTP — {fullReport?.sftpSites?.length
                  ? fullReport.sftpSites.map(s => s.name).join(', ')
                  : 'configured sites'}
              </>
            ) : (
              recipientType === 'all'
                ? 'All original recipients'
                : `${selectedEmailIds.length} email(s), ${selectedGroupIds.length} group(s)`
            )}
          </span>
        </div>
      </div>
    </div>
  );

  const stepTitles = {
    1: `Resend: ${report.name} — Select Date`,
    2: `Resend: ${report.name} — Select File`,
    3: `Resend: ${report.name} — Select Recipients`,
    4: `Resend: ${report.name} — Confirm`
  };

  const handleBack = () => {
    if (step === 4 && isSftp) {
      setStep(2); // Skip step 3 for SFTP
    } else {
      setStep(step - 1);
    }
  };

  const renderFooter = () => (
    <div className="modal-actions">
      {step > 1 && (
        <Button variant="secondary" onClick={handleBack} disabled={submitting}>
          Back
        </Button>
      )}
      <Button variant="secondary" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      {step === 1 && (
        <Button variant="primary" onClick={handleDateSubmit} disabled={!date}>
          Next
        </Button>
      )}
      {step === 3 && (
        <Button
          variant="primary"
          onClick={() => setStep(4)}
          disabled={!hasSelectedRecipients}
        >
          Review
        </Button>
      )}
      {step === 4 && (
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
          icon={<Send size={16} />}
        >
          Confirm Resend
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={stepTitles[step]}
      size="lg"
      closeOnBackdrop={!submitting}
      footer={renderFooter()}
    >
      {error && (
        <div className="resend-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </Modal>
  );
}

ResendReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired
  }),
  onSuccess: PropTypes.func
};
